/**
 * @module Provides timing and scheduling functions, such as debounce.
 */

import { window } from './globals';

/**
 * Generic function for debouncing some type of event handler or function. Debounce = only perform an action after specific period of time after an event stream stops.
 * @param scheduler A scheduling function that determines when the handler will run.
 * @param clearScheduler A function that will cancel the waiting handler.
 * @param handler The function to be run when the scheduler dictates.
 * @param timeout An optional timeout for the scheduling function.
 *
 * @example debounceScheduler(setTimeout, clearTimeout, (event) => console.log('event', event), 1000);
 * @example debounceScheduler(requestAnimationFrame, cancelAnimationFrame, (event) => console.log('event', event));
 */
export function debounceScheduler<T extends Function>(
	scheduler: (fn: T, ...params: any[]) => number,
	clearScheduler: (number: number) => void,
	handler: T,
	timeout: number = 500
): CancellableFunction {
	let scheduleId = 0;
	const caller = (...args: any[]) => {
		clearScheduler(scheduleId);
		scheduleId = scheduler.apply(null, [handler.bind(null, ...args), timeout]);
	};

	(caller as CancellableFunction).cancel = () => clearScheduler(scheduleId);

	return caller as CancellableFunction;
}

export const debounce = {
	timeout: debounceScheduler.bind(null, setTimeout, clearTimeout) as <T extends Function>(
		handler: T,
		timeout?: number
	) => CancellableFunction,
	animationFrame: debounceScheduler.bind(
		null,
		window.requestAnimationFrame,
		window.cancelAnimationFrame
	) as <T extends Function>(handler: T, timeout?: number) => CancellableFunction
};

export interface CancellableFunction {
	cancel: () => void;
	(...args: any[]): void;
}
